/**
 * Helper function that executes the message send operation.
 */

import { addOptimisticMessage } from '@saaga/shared/store/slices/chat.slice'
import { trackEvent } from '@saaga/shared/utils/analytics.util'

import { playMessageSound } from '@/utils/notification-sound.util'

import { createOptimisticEntity } from './chat-message-entity-factory.helper'
import type { SendMessageBaseParams } from './chat-message-send.interface'
import { sendViaHttp } from './chat-message-send-http.helper'
import { sendViaWebSocket } from './chat-message-send-websocket.helper'

export interface ExecuteSendParams extends SendMessageBaseParams {
  content: string
  isNote: boolean
  attachmentIdList?: string[]
}

const dispatchOptimisticMessage = (
  params: ExecuteSendParams,
  optimisticEntity: ReturnType<typeof createOptimisticEntity>
): void => {
  params.dispatch(
    addOptimisticMessage({
      conversationId: params.conversationId ?? 'pending',
      message: optimisticEntity,
    })
  )
}

const sendViaWebSocketOnly = (
  params: ExecuteSendParams,
  optimisticEntity: ReturnType<typeof createOptimisticEntity>
): void => {
  const { websocket } = params
  if (!websocket) return

  sendViaWebSocket({
    content: params.content,
    optimisticEntity,
    conversationId: params.conversationId,
    agencySlug: params.agencySlug,
    websocket,
  })
}

export const executeSendMessage = async (params: ExecuteSendParams): Promise<void> => {
  const optimisticEntity = createOptimisticEntity({
    content: params.content,
    type: params.isNote ? 'note' : 'message',
    conversationId: params.conversationId,
    user: params.user,
  })

  dispatchOptimisticMessage(params, optimisticEntity)
  playMessageSound()
  trackEvent('message_sent', {
    hasAttachments: (params.attachmentIdList?.length ?? 0) > 0,
    agencySlug: params.agencySlug ?? null,
  })

  const hasAttachments = params.attachmentIdList && params.attachmentIdList.length > 0

  if (!params.isNote && !hasAttachments && params.websocket?.isConnected) {
    sendViaWebSocketOnly(params, optimisticEntity)
    return
  }

  await sendViaHttp({
    content: params.content,
    isNote: params.isNote,
    optimisticEntity,
    conversationId: params.conversationId,
    agencySlug: params.agencySlug,
    dispatch: params.dispatch,
    sendChatMessage: params.sendChatMessage,
    onNewConversation: params.onNewConversation,
    attachmentIdList: params.attachmentIdList,
  })
}
